/**
 * Authentication Type Definitions
 * Family-focused types for user authentication and authorization
 */

/**
 * User role enumeration
 * Defines the user types in the family app:
 * - SUPER_ADMIN: Family founder — highest permission within the family
 * - PARENT: Full system access, family management, all features
 * - CHILD: Age-appropriate access, assigned tasks, personal calendar
 *
 * Rank hierarchy: SUPER_ADMIN(3) > PARENT(2) > CHILD(1) — see roleRank().
 */
export enum UserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  PARENT = 'PARENT',
  CHILD = 'CHILD',
}

/**
 * Type guard to check if a value is a valid UserRole
 */
export function isUserRole(value: unknown): value is UserRole {
  return (
    typeof value === 'string' &&
    Object.values(UserRole).includes(value as UserRole)
  );
}

/**
 * Get all user role values as an array
 */
export function getAllUserRoles(): readonly UserRole[] {
  return Object.values(UserRole);
}

/**
 * User entity representing an authenticated user
 */
export interface User {
  readonly id: string;
  readonly username: string;
  readonly email: string;
  readonly name: string;
  readonly role: UserRole;
  readonly permissions: readonly string[];
  readonly createdAt: string;
  readonly lastLoginAt?: string;
  /** Public URL of the user's avatar image (Supabase `avatars` bucket). */
  readonly avatarUrl?: string;
}

/**
 * Login credentials for authentication
 */
export interface LoginCredentials {
  readonly username: string;
  readonly password: string;
  readonly rememberMe?: boolean;
}

/**
 * Registration data for new user creation.
 *
 * Family creation is a mandatory pre-requirement: a signup either founds a
 * new family (`familyName`, account becomes SUPER_ADMIN) or joins one via an
 * invitation (`inviteCode`). The role is NEVER chosen at signup — founders
 * are always SUPER_ADMIN and invited users receive the inviter-assigned role.
 */
export interface RegisterData {
  readonly name: string;
  readonly email: string;
  readonly username: string;
  readonly password: string;
  readonly confirmPassword: string;
  /** Founder path: the family to create (mutually exclusive with inviteCode). */
  readonly familyName?: string | undefined;
  /** Invited path: invitation code from a shared join link. */
  readonly inviteCode?: string | undefined;
}

/**
 * Supported third-party OAuth providers for social sign-in.
 */
export type OAuthProvider = 'google' | 'apple' | 'github';

/**
 * API response for authentication requests
 */
export interface AuthResponse {
  readonly user: User;
  readonly token: string;
  readonly refreshToken?: string;
  readonly expiresIn: number;
}

/**
 * JWT token payload structure
 */
export interface TokenPayload {
  readonly sub: string; // User ID
  readonly username: string;
  readonly role: UserRole;
  readonly permissions: readonly string[];
  readonly iat: number; // Issued at
  readonly exp: number; // Expiration
}

/**
 * Global authentication state
 */
export interface AuthState {
  readonly user: User | null;
  readonly token: string | null;
  readonly refreshToken: string | null;
  readonly isAuthenticated: boolean;
  readonly isInitialized: boolean;
  /**
   * True only when the live session was established by a password-recovery
   * link (GoTrue `PASSWORD_RECOVERY`). `/reset-password` gates on it so an
   * ordinary signed-in session cannot set a new password without knowing the
   * current one — that path is `changePassword`, which re-authenticates.
   * NEVER persisted: it must not outlive the session that produced it.
   */
  readonly isRecoverySession: boolean;
  readonly loading: boolean;
  readonly error: string | null;
  readonly rememberMe: boolean;
  readonly redirectPath: string | null;
}

/**
 * Role-based permissions mapping
 * Defines what each role can access in the family app
 */
export interface RolePermissions {
  readonly [UserRole.SUPER_ADMIN]: readonly string[];
  readonly [UserRole.PARENT]: readonly string[];
  readonly [UserRole.CHILD]: readonly string[];
}

/**
 * Password change request (parent only)
 */
export interface ChangePasswordRequest {
  readonly userId: string;
  readonly newPassword: string;
}

/**
 * Forgot password request
 */
export interface ForgotPasswordRequest {
  readonly username: string;
}

/**
 * Auth actions interface for store
 */
export interface AuthActions {
  login: (credentials: LoginCredentials) => Promise<void>;
  logout: () => Promise<void>;
  register: (data: RegisterData) => Promise<void>;
  refreshAuthToken: () => Promise<void>;
  checkAuth: () => Promise<void>;
  clearError: () => void;
  setRedirectPath: (path: string | null) => void;
  /** Start a social sign-in flow (redirects to the provider in Supabase mode). */
  signInWithOAuth: (provider: OAuthProvider) => Promise<void>;
  /** Complete an OAuth session after returning from the provider callback. */
  completeOAuthSession: () => Promise<void>;
  /**
   * Finalise an email-invited account (/accept-invite): sets the chosen
   * name, username and password on the already-established invite session.
   */
  completeInvite: (data: {
    readonly name: string;
    readonly username: string;
    readonly password: string;
  }) => Promise<void>;
  /**
   * Subscribe to live Supabase auth-state changes (sign-in, sign-out, token
   * refresh) so the store mirrors them across tabs and on background refresh.
   * Returns an unsubscribe function.
   */
  initAuthListener: () => () => void;
  /** Persist a new avatar URL for the current user and update the store. */
  updateAvatar: (avatarUrl: string) => Promise<void>;
  /**
   * Update the in-memory user's role. Called by the family store when the
   * active family changes, since the effective role is per-family membership.
   */
  setUserRole: (role: UserRole) => void;
}

/**
 * Complete auth store interface
 */
export type AuthStore = AuthState & AuthActions;
